import { NavLink } from 'react-router';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Mail,
  Settings,
  LogOut,
  Sprout,
  GraduationCap,
  Leaf,
  Activity,
  BarChart3,
  Bot,
  FileText,
  Tag,
  TrendingUp,
  BookOpen,
  Library,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/contexts/auth-context';
import { useLogout } from '@/hooks/use-auth';
import { usePendingReportsCount } from '@/hooks/use-reports';
import { cn } from '@/lib/utils';

const baseNavItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/students', label: 'Students', icon: Users },
  { to: '/assignments', label: 'Assignments', icon: ClipboardList },
  { to: '/reports', label: 'Reports', icon: FileText },
];

const seedsNavItems = [
  { to: '/seeds', label: 'Catalog', icon: Leaf },
  { to: '/practice-sessions', label: 'Sessions', icon: Activity },
];

const adminSeedsNavItems = [
  { to: '/seeds/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/ai-interactions', label: 'AI Logs', icon: Bot },
];

const crmNavItems = [
  { to: '/tags', label: 'Tags', icon: Tag },
  { to: '/funnel', label: 'Funnel', icon: TrendingUp },
];

const contentNavItems = [
  { to: '/content', label: 'Content', icon: Library },
];

const bottomNavItems = [
  { to: '/invitations', label: 'Invitations', icon: Mail },
  { to: '/docs', label: 'Docs', icon: BookOpen },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const adminNavItems = [
  { to: '/teachers', label: 'Teachers', icon: GraduationCap },
];

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuthContext();
  const logout = useLogout();
  const { data: pendingCount } = usePendingReportsCount();
  const pendingReports = pendingCount?.pending ?? 0;

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2 px-4 py-6">
        <Sprout className="h-7 w-7 text-primary" />
        <span className="text-lg font-semibold">Karmic Practice</span>
      </div>

      <nav className="flex-1 space-y-1 px-2">
        {baseNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <span className="flex items-center gap-3">
              <item.icon className="h-4 w-4" />
              {item.label}
            </span>
            {item.to === '/reports' && pendingReports > 0 && (
              <Badge variant="secondary" className="ml-auto">
                {pendingReports}
              </Badge>
            )}
          </NavLink>
        ))}

        <div className="pt-2">
          <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Seeds
          </p>
          {[...seedsNavItems, ...(user?.role === 'admin' ? adminSeedsNavItems : [])].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="pt-2">
          <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Learning Hub
          </p>
          {contentNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="pt-2">
          <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            CRM
          </p>
          {crmNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="pt-2">
          {[...bottomNavItems, ...(user?.role === 'admin' ? adminNavItems : [])].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="border-t px-4 py-4">
        <div className="mb-2 text-sm">
          <p className="font-medium">
            {user?.first_name ?? user?.email ?? 'Teacher'}
          </p>
          <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start gap-2" onClick={logout}>
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
}
